/**
 * Tourist-tax patterns. Greek law uses per_room_per_night (the
 * Climate Resilience Fee / Φόρος Διαμονής) as a flat amount based on
 * the property's keys and the season. The other basis types are kept
 * generic so we can extend to other markets later without a schema
 * change.
 */
export const TAX_BASIS = {
  per_person_per_night: "per_person_per_night",
  per_room_per_night: "per_room_per_night",
  per_stay: "per_stay",
  percent_of_total: "percent_of_total",
} as const;

export type TaxBasis = keyof typeof TAX_BASIS;

export const TAX_BASIS_LABELS: Record<TaxBasis, string> = {
  per_person_per_night: "Per guest, per night",
  per_room_per_night: "Per accommodation, per night",
  per_stay: "Flat fee per stay",
  percent_of_total: "Percentage of booking total",
};

export const TAX_BASIS_DESCRIPTIONS: Record<TaxBasis, string> = {
  per_person_per_night:
    "Multiplied by guests × nights. Common pattern in most EU countries.",
  per_room_per_night:
    "Greek Climate Resilience Fee (Φόρος Διαμονής) — flat amount per night based on the property's number of keys (1-key €1.50/€0.50, 2-key €3/€0.50, 3-key €7/€1.50, 4-key €7/€1.50, 5-key €10/€4) for peak (Apr–Oct) / off-peak (Nov–Mar). Multiplied by nights only.",
  per_stay: "Flat fee added once per booking, regardless of guests or nights.",
  percent_of_total: "Calculated as a percentage of the stay subtotal.",
};

/** Reference tax brackets for non-Greek properties. Greek hosts should
 *  use the auto-calculator (climateResilienceCategory on the listing,
 *  rates in src/lib/climate-resilience-fee.ts) — these manual entries
 *  exist for other EU markets that haven't been special-cased yet.
 *  Amounts are in minor units (cents). */
export const TAX_EXAMPLES: Array<{
  country: string;
  region?: string;
  basis: TaxBasis;
  amount: number;
  notes: string;
}> = [
  {
    country: "Greece",
    region: "All categories",
    basis: "per_room_per_night",
    amount: 0,
    notes:
      "Use the auto-calculator above — rates vary by category (Γ.1 €8/€2, Γ.2 ≥80m² €15/€4, hotels by class) and season.",
  },
];

import {
  computeClimateResilienceFee,
  CLIMATE_RESILIENCE_CATEGORIES,
  type ClimateResilienceCategory,
} from "./climate-resilience-fee";

type TaxConfig = {
  basis: string | null;
  amount: number | null;
  maxNights: number | null;
  minAge: number | null;
  legacyPerGuestPerNight: number | null;
  /** Greek Climate Resilience auto-calculator. When set, overrides the
   *  manual `amount` and uses the booking's date range to split nights
   *  between high (Apr–Oct) and low (Nov–Mar) season rates. */
  climateResilienceCategory?: string | null;
};

type DateRange = {
  checkIn?: Date | string | null;
  checkOut?: Date | string | null;
};

function isClimateCategory(s: string | null | undefined): s is ClimateResilienceCategory {
  return (
    typeof s === "string" &&
    (CLIMATE_RESILIENCE_CATEGORIES as readonly string[]).includes(s)
  );
}

/**
 * Compute the tax (in minor units of the booking currency) for a stay.
 * subtotal is used for percent_of_total. Returns 0 if no tax configured.
 *
 * If `climateResilienceCategory` is set on the listing, this is the
 * Greek Climate Resilience Fee and we ignore the manual `amount`,
 * computing the per-night rate from the booking's specific date range.
 */
export function computeTouristTax(
  config: TaxConfig,
  guests: number,
  nights: number,
  subtotal: number,
  range?: DateRange,
): number {
  // Greek Climate Resilience Fee — auto-calculator.
  if (isClimateCategory(config.climateResilienceCategory) && range?.checkIn && range?.checkOut) {
    return computeClimateResilienceFee(
      config.climateResilienceCategory,
      range.checkIn,
      range.checkOut,
    ).total;
  }

  // Backward compatibility — old listings only had touristTaxPerGuestPerNight
  if (!config.basis && config.legacyPerGuestPerNight) {
    return config.legacyPerGuestPerNight * guests * nights;
  }
  if (!config.basis || !config.amount || config.amount <= 0) return 0;

  const cappedNights =
    config.maxNights && config.maxNights > 0 ? Math.min(nights, config.maxNights) : nights;

  switch (config.basis as TaxBasis) {
    case "per_person_per_night":
      return config.amount * guests * cappedNights;
    case "per_room_per_night":
      return config.amount * cappedNights;
    case "per_stay":
      return config.amount;
    case "percent_of_total":
      // amount is basis points (500 = 5%)
      return Math.round((subtotal * config.amount) / 10000);
    default:
      return 0;
  }
}
